// ─────────────────────────────────────────────────────────────────────
// MONEY — Norwegian-krone formatting shared by every screen that shows an
// amount. The app has historically inlined `${n} kr`; new money-facing code
// (Reporting, settlements) goes through these helpers so thousands separators
// and decimals are consistent everywhere.
// ─────────────────────────────────────────────────────────────────────

const nbNO = (decimals: number) =>
  new Intl.NumberFormat('nb-NO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Format an amount in NOK for display: `1 250 kr`, `12,50 kr`.
 * Whole-krone amounts render without decimals unless `decimals` forces them.
 * Non-finite input renders as `0 kr` (never `NaN kr` on a receipt).
 */
export function formatNok(amount: number, decimals?: number): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const d = decimals ?? (Math.abs(n - Math.round(n)) < 0.005 ? 0 : 2);
  // Intl uses a non-breaking thin space as the nb-NO group separator; a regular
  // NBSP keeps copy/paste into spreadsheets predictable.
  const s = nbNO(d)
    .format(n)
    .replace(/[\u00A0\u202F\s]/g, '\u00A0');
  return `${s}\u00A0kr`;
}

/** Round to whole øre (two decimals) without floating-point drift. */
export function roundOre(amount: number): number {
  return Math.round((Number.isFinite(amount) ? amount : 0) * 100) / 100;
}
